"use client";

import { Button } from "daskapital-ds";
import DisplayImage from "@/components/common/DisplayImage/DisplayImage";
import styled from "styled-components";

export type IBlockCtaBanner = {
  attrs: {
    anchor?: string;
    mediaUrl?: string;
    mediaAlt?: string;
    title?: string;
    description?: string;
    backgroundColor?: string;
    textColor?: string;
    buttonVariant?: "primary" | "secondary" | "tertiary" | "link";
    buttonText?: string;
    buttonHref?: {
      url?: string;
      newTab?: boolean;
    };
    className?: string;
  };
};

const StyledCtaBanner = styled.div<{ $bg?: string; $color?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  border-radius: var(--radius-md, 12px);
  background-color: ${({ $bg }) => $bg || "var(--color-secondary50)"};

  @media only screen and (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    min-height: 240px;
  }

  .cta-banner__image {
    position: relative;
    width: 100%;
    min-height: 240px;
    background-color: var(--color-gray90);
    clip-path: polygon(100% 0, 100% 36%, 100% 66%, 65% 100%, 0 82%, 0 0);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @media only screen and (min-width: 768px) {
      flex: 0 0 32%;
      align-self: stretch;
      min-width: 300px;
      clip-path: polygon(86% 0, 94% 39%, 88% 100%, 0 100%, 0 59%, 0 0);
    }

    @media only  screen and (min-width: 920px) {
      min-width: 400px;
    }

     @media only screen and (min-width: 1200px) {
       min-width: 400px;
    }
  }

  .cta-banner__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    margin-bottom: 8px;

     @media only screen and (min-width: 768px) {
      margin-bottom: 0;
    }
  }

  .cta-banner__title {
    font-family: var(--font-alternative);
    margin: 0;
    color: ${({ $color }) => $color || "var(--color-gray90)"};
    font-size: 28px;
    line-height: 1.4;
    font-weight: 700;
    text-align: left;

    @media only screen and (min-width: 768px) {
      font-size: 32px;
    }
  }

  .cta-banner__description {
    margin: 0;
    color: ${({ $color }) => $color || "var(--color-gray90)"};
    font-size: 16px;
    line-height: 1.7;
    text-align: left;

    @media only screen and (min-width: 728px) {
      padding-right: 200px;
    }
  }

  .cta-banner__action {
    display: flex;
    padding: 16px;

    button {
      width: 100%;
      justify-content: center;
    }

    @media only screen and (min-width: 768px) {
      display; initial;
      padding: 0;
      width: initial;
      position: absolute;
      right: 16px;
      bottom: 16px;
    }
  }
`;

export default function BlockCtaBanner({ attrs }: IBlockCtaBanner) {
  const {
    anchor,
    mediaUrl,
    mediaAlt,
    title,
    description,
    backgroundColor,
    textColor,
    buttonVariant = "primary",
    buttonText,
    buttonHref,
    className,
  } = attrs || {};

  const url = buttonHref?.url;
  const newTab = buttonHref?.newTab;
  const linkTarget = newTab ? "_blank" : undefined;

  return (
    <StyledCtaBanner
      id={anchor || undefined}
      className={className}
      $bg={backgroundColor}
      $color={textColor}
    >
      {mediaUrl && (
        <div className="cta-banner__image">
          <DisplayImage
            url={mediaUrl}
            alt={mediaAlt}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
          />
        </div>
      )}

      <div className="cta-banner__content">
        {title && <h2 className="cta-banner__title">{title}</h2>}
        {description && (
          <p className="cta-banner__description">{description}</p>
        )}
      </div>

      {buttonText && (
        <div className="cta-banner__action">
          <Button
            variant={buttonVariant}
            href={url || undefined}
            target={url ? linkTarget : undefined}
            rel={url && linkTarget ? "noreferrer noopener" : undefined}
          >
            {buttonText}
          </Button>
        </div>
      )}
    </StyledCtaBanner>
  );
}
